import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import { getMasterDropdownService, getCitiesBasedOnState } from "@/services/masters";
import FullScreenLocationPicker from "@/components/common/LocationSelector";
import { fontSize, fontWeight, textColor, bgColor, borderColor } from "@/theme";

export const adminSchoolSchema = z.object({
  School_Name: z.string().min(2, "School name must be at least 2 characters"),
  Address: z.string().min(5, "Address must be at least 5 characters"),
  State: z.string().min(1, "Please select a state"),
  City: z.string().min(1, "Please select a district"),
  School_Branch: z.string().min(1, "Please select a school branch"),
  Latitude: z.string().min(1, "Latitude is required"),
  Longitude: z.string().min(1, "Longitude is required"),
Status: z.string().optional(),
});

export const parentSchoolSchema =
  z.object({
    School_Name:
      z.string().min(1),

    State:
      z.string().min(1),

    City:
      z.string().min(1),

    Address:
      z.string().min(1),

    School_Branch:
      z.string().optional(),

    Latitude:
      z.string(),

    Longitude:
      z.string(),
  });

export type AdminSchoolFormData =
  z.infer<typeof adminSchoolSchema>;

export type SchoolFormData =
  z.infer<typeof parentSchoolSchema>;

interface AddSchoolFormProps {
  pendingCity?: string;
  defaultState?: string;
  defaultCity?: string;
  isParent?: boolean;
  onSuccess?: (school: {
    name: string;
    state: string;
    city: string;
  }) => void;
  isEdit?: boolean;
  currentStatus?: "Pending" | "Approved";
}

const AddSchoolForm = ({
  pendingCity = "",
  defaultState = "",
  defaultCity = "",
  isParent = false,
  isEdit = false,
  currentStatus = "Pending",
}: AddSchoolFormProps) => {
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const pendingCityRef = useRef<string>("");
  const pendingStateRef = useRef<string>("");   // ← ADD THIS
  const rawStatesRef = useRef<any[]>([]);
  const rawCitiesRef = useRef<any[]>([]);

  const {
    control,
    formState: { errors },
    clearErrors,
    watch,
    setValue,
    register
  } = useFormContext<SchoolFormData & { Status?: string }>();

  useEffect(() => {
    if (defaultState) {
      pendingStateRef.current = defaultState;

      setValue("State", defaultState, {
        shouldValidate: true,
      });
    }

    if (defaultCity) {
      pendingCityRef.current = defaultCity;
    }
  }, [
    defaultState,
    defaultCity,
    setValue,
  ]);
  const selectedState = watch("State");
  const selectedCity = watch("City");
  // ── Capture defaults into refs immediately (before any async) ────────────
  // Don't call setValue here — options aren't loaded yet.

  // ── Non-parent pendingCity prop ───────────────────────────────────────────
  useEffect(() => {
    if (!isParent && pendingCity) {
      pendingCityRef.current = pendingCity;
    }
  }, [pendingCity, isParent]);

  const lat = watch("Latitude");
  const lng = watch("Longitude");
  const addr = watch("Address");

  const handleLocationChange = (loc: { latitude: string; longitude: string; address: string }) => {
    setValue("Latitude", loc.latitude, { shouldValidate: true });
    setValue("Longitude", loc.longitude, { shouldValidate: true });
    setValue("Address", loc.address, { shouldValidate: true });
    clearErrors(["Latitude", "Longitude", "Address"]);
  };


  // ── Load states, then apply pendingState once options exist ──────────────
  useEffect(() => {
    async function fetchMasterData() {
      try {
        const response = await getMasterDropdownService();
        if (response.status === 200 && response.data) {
          const data = response.data as any;
          const stateList = data.States || data.states || [];
          rawStatesRef.current = stateList;
          setStates(stateList);

          // ✅ NOW options exist — safe to setValue
          if (pendingStateRef.current) {
            const matchedState = stateList.find(
              (s: any) =>
                s.unique_code === pendingStateRef.current ||
                s.Name === pendingStateRef.current
            );

            setValue(
              "State",
              matchedState?.Name || pendingStateRef.current,
              {
                shouldValidate: true,
              }
            );

            pendingStateRef.current = "";
          }
        }
      } catch (error) {
        // handle
      } finally {
        setLoadingData(false);
      }
    }
    fetchMasterData();
  }, []);   // runs once on mount

  // ── Fetch cities when selectedState changes, then apply pendingCity ───────
  useEffect(() => {
    if (!selectedState || loadingData) {
      if (!selectedState) setCities([]);
      return;
    }

    setLoadingCities(true);

    const stateCode =
      rawStatesRef.current.find((s: any) => s.Name === selectedState)?.unique_code ||
      selectedState;

    getCitiesBasedOnState(stateCode)
      .then(({ data }) => {
        const res = data as any;

        const raw: any[] = Array.isArray(res?.Cities)
          ? res.Cities
          : [];

        rawCitiesRef.current = raw;

        const cityList = raw.map((c: any, idx: number) => ({
          id: c.id ?? idx,
          label: c.City_Name,
          value: c.City_Name,
        }));

        setCities(cityList);

        // SET CITY AFTER OPTIONS LOAD
        if (pendingCityRef.current) {
          const pending = pendingCityRef.current;
          const matchedCity = raw.find(
            (c: any) =>
              c.unique_code === String(pending) ||
              c.City_Name === pending
          );

          if (matchedCity) {
            setValue(
              "City",
              matchedCity.City_Name,
              {
                shouldValidate: true,
              }
            );
          }

          pendingCityRef.current = "";
        }
        else if (
          selectedCity &&
          !cityList.some(
            c => c.value === selectedCity
          )
        ) {
          setValue("City", "");
          setValue("School_Branch", "");
        }
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selectedState, loadingData]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          label="School Name"
          name="School_Name"
          type="text"
          error={errors.School_Name?.message}
          onClear={() => clearErrors("School_Name")}
          required
          placeholder="Enter school name"
        />
        <Dropdown
          name="State"
          label="State"
          options={states.map((s: any) => ({ id: s.id, label: s.Name, value: s.Name }))}
          control={control}
          error={errors.State?.message}
          required
          isLoading={loadingData}
          placeholder="Select a state"
          onClear={() => clearErrors("State")}
          disabled={isParent}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Dropdown
          name="City"
          label="District"
          options={cities}
          control={control}
          error={errors.City?.message}
          required
          disabled={!selectedState || isParent}
          isLoading={loadingData || loadingCities}
          placeholder="Select a district"
          onClear={() => clearErrors("City")}
        />

        {!isParent && (
          <FormField
            control={control}
            label="School Branch"
            name="School_Branch"
            type="text"
            error={errors.School_Branch?.message}
            onClear={() => clearErrors("School_Branch")}
            required
            placeholder="Enter school branch"
          />
        )}
      </div>
      <FormField
        control={control}
        label="Address"
        name="Address"
        isTextarea
        error={errors.Address?.message}
        onClear={() => clearErrors("Address")}
        required
      />
      {isEdit && currentStatus === "Pending" && (
  <label className="flex items-center gap-3 mt-4">
    <input
      type="checkbox"
      onChange={(e) => {
        setValue("Status", e.target.checked ? "Approved" : "Pending");
      }}
    />
    <span>Approve this school</span>
  </label>
)}

      <div>
        <p className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-2`}>
          School Location <span className="text-red-500">*</span>
        </p>
        {addr ? (
          <div className={`px-3 py-2.5 ${bgColor.gray50} border ${borderColor.gray200} rounded-lg ${fontSize.sm} ${textColor.body} mb-2 flex items-start gap-2`}>
            <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <span>{addr}</span>
          </div>
        ) : (
          errors.Address && (
            <p className="text-xs text-red-500 mb-2">{errors.Address.message}</p>
          )
        )}
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-semibold"
        >
          <MapPin className="w-4 h-4" />
          {lat && lng ? "Change Location" : "Select Location"}
        </button>
      </div>

      <FullScreenLocationPicker
        isOpen={mapOpen}
        onClose={() => setMapOpen(false)}
        value={
          lat && lng
            ? { latitude: lat, longitude: lng }
            : undefined
        }
        onConfirm={(loc) => {
          handleLocationChange(loc);
          setMapOpen(false);
        }}
        selectedState={
          rawStatesRef.current.find(
            (s: any) =>
              s.Name === selectedState ||
              s.unique_code === selectedState
          )?.Name || ""
        }
        selectedCity={
          cities.find(c => c.value === selectedCity)?.label || ""
        }
      />
    </div>
  );
};

AddSchoolForm.displayName = "AddSchoolForm";

export default AddSchoolForm;
